import React from 'react'
import {Alert, Box, Chip, CircularProgress, Paper, Stack, Typography} from '@mui/material'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useGetPaymentIframeQuery} from 'store/Store'
import useResponsive from 'logic/useResponsive'

const Payment = () => {
    const {t} = useTranslation()
    const {token} = useParams()
    const {smallScreen} = useResponsive()
    const {
        data: iframe,
        isLoading,
        error,
    } = useGetPaymentIframeQuery(token, {
        skip: !token,
    })

    const status = (iframe?.status || '').toLowerCase()
    const amount = iframe?.amount
    const currency = iframe?.currency
    const paymentId = iframe?.paymentId || iframe?.payment?.id

    const content = (
        <Stack spacing={2.5}>
            <Box>
                <Typography variant="h5" gutterBottom>
                    {t('paymentFlow.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('paymentFlow.subtitle')}
                </Typography>
                {isLoading && (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress />
                    </Box>
                )}
            </Box>

            {status === 'token_expired' && (
                <Alert severity="warning">{t('paymentFlow.missingToken')}</Alert>
            )}

            {error && (
                <Alert severity="error">{error?.message || t('paymentFlow.errors.status')}</Alert>
            )}

            {!token && !isLoading && !error && (
                <Alert severity="warning">{t('paymentFlow.missingToken')}</Alert>
            )}

            {status && (
                <Stack spacing={1}>
                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        spacing={1}
                        alignItems="flex-start">
                        <Chip
                            label={t(`paymentFlow.result.${status}`, {defaultValue: status})}
                            color={
                                {success: 'success', failed: 'error', pending: 'warning'}[status] ||
                                'default'
                            }
                        />
                        {paymentId && (
                            <Typography variant="body2" color="text.secondary">
                                {t('paymentFlow.details.paymentId')}: {paymentId}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            )}

            {status === 'pending' && (
                <Box
                    component="iframe"
                    src={iframe?.url}
                    title="payment"
                    sx={{
                        width: '100%',
                        minHeight: 520,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                    }}
                />
            )}
            {status === 'success' && (
                <Alert severity="success">
                    <Stack spacing={1}>
                        <Typography variant="subtitle1">
                            {t('paymentFlow.result.success')}
                        </Typography>
                        {amount && (
                            <Typography variant="body2">
                                {t('paymentFlow.details.amount')}:{' '}
                                <Chip label={`${amount} ${currency || ''}`.trim()} size="small" />
                            </Typography>
                        )}
                    </Stack>
                </Alert>
            )}
        </Stack>
    )

    return (
        <Box minHeight="100dvh" display="flex" alignItems="center" justifyContent="center">
            {smallScreen ? (
                <Box width="100%" px={2}>
                    {content}
                </Box>
            ) : (
                <Paper elevation={6} sx={{maxWidth: 640, width: '100%', p: {xs: 3, md: 4}}}>
                    {content}
                </Paper>
            )}
        </Box>
    )
}

export default Payment
